import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const Services: React.FC = () => {
  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    gsap.utils.toArray('.service-card').forEach((card: any, index) => {
      gsap.fromTo(card, 
        { y: 50, opacity: 0, rotationY: 15 },
        {
          y: 0,
          opacity: 1,
          rotationY: 0,
          duration: 0.8,
          delay: index * 0.1,
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            end: 'bottom 15%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });
  }, []);

  const services = [
    {
      title: "Premium Wine Sourcing",
      description: "Access to exclusive wine collections from renowned vineyards and wineries worldwide.",
      icon: <svg className="w-12 h-12 icon-primary" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>,
      features: ["Boutique Wineries", "Rare Vintages", "Expert Curation", "Global Network"]
    },
    {
      title: "Fine Dining Ingredients",
      description: "Curated selection of premium ingredients for high-end culinary establishments.",
      icon: <svg className="w-12 h-12 icon-primary" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
      </svg>,
      features: ["Artisan Products", "Organic Options", "Seasonal Availability", "Chef Partnerships"]
    },
    {
      title: "Supplier Network Management",
      description: "Comprehensive network of verified suppliers across various food categories.",
      icon: <svg className="w-12 h-12 icon-primary" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
      </svg>,
      features: ["Quality Verification", "Supply Chain Management", "Direct Relationships", "Competitive Pricing"]
    },
    {
      title: "Custom Sourcing Solutions",
      description: "Tailored sourcing solutions for specific culinary needs and requirements.",
      icon: <svg className="w-12 h-12 icon-primary" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>,
      features: ["Bespoke Solutions", "Specialty Items", "Bulk Orders", "Exclusive Access"]
    }
  ];

  return (
    <section id="services" className="section-container section-bg">
      <div className="section-content">
        {/* Section Header */}
        <div className="section-header animate-on-scroll">
          <h2 className="heading-lg heading-on-light mb-6">
            Our Services
          </h2>
          <p className="text-body-lg text-on-light max-w-3xl mx-auto">
            We provide comprehensive food sourcing solutions tailored to the needs of the wine and dine industry.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid-4 mb-16">
          {services.map((service, index) => (
            <div key={index} className="service-card card-hover">
              <div className="card-content-lg">
              <div className="flex justify-center mb-4">{service.icon}</div>
                <h3 className="heading-xs heading-on-light text-accent-bold text-on-light mb-4 text-center">{service.title}</h3>
                <p className="text-body-sm text-on-light mb-6 text-center">{service.description}</p>
              <ul className="space-y-2">
                {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-body-xs text-on-light">
                      <svg className="w-4 h-4 icon-primary mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center animate-on-scroll">
          <h3 className="heading-md heading-on-light mb-6">
            Ready to Transform Your Sourcing?
            </h3>
          <p className="text-body text-on-light cta-content mb-8">
            Join our network of premium suppliers and culinary establishments. Let us help you find exactly what you need.
            </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" className="btn-primary text-lg px-10 py-4">
              Browse Our Products
            </Link>
            <a href="#contact" className="btn-outline text-lg px-10 py-4">
              Get Started Today
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
